import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * The pinned footer grows when the upsells carousel loads, which happens after the drawer has
   * already opened, so its height has to be re-measured rather than only sampled on open.
   * @type {ResizeObserver}
   */
  #footerResizeObserver = new ResizeObserver(() => this.#updateStickyState());

  /**
   * The footer the observer is currently watching. An empty cart renders no footer at all, so it
   * cannot be observed once at connect time — it is picked up on the first measurement instead.
   * @type {Element | null}
   */
  #observedFooter = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#footerResizeObserver.disconnect();
    this.#observedFooter = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    // The whole pinned block is measured, not just the summary — the upsells carousel sits inside
    // it and is a meaningful part of the height on small screens.
    const footer = dialog.querySelector('.cart-drawer__footer');

    if (!content || !footer) {
      // Ensure the dialog doesn't get stuck in "pinned" mode when the footer disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    if (this.#observedFooter !== footer) {
      if (this.#observedFooter) this.#footerResizeObserver.unobserve(this.#observedFooter);
      this.#footerResizeObserver.observe(footer);
      this.#observedFooter = footer;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const footerHeight = footer.getBoundingClientRect().height;

    // A zero height means the drawer is closed; keep the current state rather than thrashing it.
    if (!drawerHeight) return;

    const ratio = footerHeight / drawerHeight;
    const pinned = ratio > this.#summaryThreshold ? 'false' : 'true';

    // Only write when it changes, so the observer above cannot feed itself.
    if (dialog.getAttribute('cart-summary-sticky') !== pinned) {
      dialog.setAttribute('cart-summary-sticky', pinned);
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
